import { useCallback, useEffect, useMemo, useState } from 'react';

import { noop, useBoolean } from '@/_shared';

import { AsyncComboBox } from './async-combo-box';
import type { RAsyncComboBoxProps, RAsyncItemsRendererProps, RAsyncShowMoreButtonRendererProps } from './types';

export type RAsyncMultiComboBoxProps<T extends { id: number }> = Omit<
  RAsyncComboBoxProps<T, false>,
  'isMulti' | 'options' | 'disabled' | 'onValueChange' | 'filterItems' | 'filterOption'
> & {
  options: T[];
  paginatedItems?: number;
  isEditable?: boolean;
  filterOptions?: (item: T) => boolean;
  onValueChange: (values: T[]) => void;
  sortValues?: (values: T[]) => T[];
  renderItems: RAsyncItemsRendererProps<T>;
  renderShowMoreButton?: RAsyncShowMoreButtonRendererProps;
};

export function RAsyncMultiCombobox<T extends { id: number }>({
  options = [],
  paginatedItems,
  isEditable = false,
  onValueChange = noop,
  filterOptions = () => false,
  renderItems,
  renderShowMoreButton,
  sortValues,
  keyExtractor,
  ...props
}: RAsyncMultiComboBoxProps<T>) {
  const [values, setValues] = useState<T[]>(options);
  const [isShowingAll, showAll] = useBoolean(!paginatedItems);

  const showAllOptions = isShowingAll || isEditable;

  const valueChangeHandler: RAsyncComboBoxProps<T, false>['onValueChange'] = (newValues) => {
    const _newValues = newValues ? [newValues as T, ...values] : [...values];
    if (sortValues) {
      onValueChange(sortValues(_newValues));
    } else {
      onValueChange(_newValues);
    }
  };

  const removeItemHandler = useCallback(
    (item: T) => {
      setValues((prev) => {
        const newValues = prev.filter((prevItem) => keyExtractor(prevItem) !== keyExtractor(item));
        onValueChange(newValues);
        return newValues;
      });
    },
    [keyExtractor, onValueChange]
  );

  useEffect(() => {
    setValues(options);
  }, [options]);

  const renderItemProps = useMemo(
    () => ({
      values: showAllOptions ? values : values.slice(0, paginatedItems),
      removeItem: removeItemHandler,
    }),
    [showAllOptions, values, paginatedItems, removeItemHandler]
  );

  const showMoreButtonProps = useMemo(
    () => ({
      onClick: showAll.toggle,
      text: isShowingAll ? 'Show less' : 'Show more',
    }),
    [isShowingAll, showAll.toggle]
  );

  const filterItemsHandler: RAsyncComboBoxProps<T, false>['filterItems'] = useCallback(
    (item: T) => !values.some((value) => keyExtractor(value) === keyExtractor(item) || filterOptions(item)),
    [keyExtractor, filterOptions, values]
  );

  return (
    <div className="flex flex-col gap-8">
      {isEditable && (
        <AsyncComboBox<T, false>
          {...props}
          isMulti={false}
          onValueChange={valueChangeHandler}
          keyExtractor={keyExtractor}
          filterItems={filterItemsHandler}
          controlShouldRenderValue={false}
        />
      )}
      <div className="text-c-card-foreground!">
        {renderItems(renderItemProps)}
        {!isEditable && paginatedItems && renderShowMoreButton && values.length > paginatedItems
          ? renderShowMoreButton?.(showMoreButtonProps)
          : null}
      </div>
    </div>
  );
}
